import { api } from "api";
import { PermissionsDto } from "api/dtos/PermissionsDto";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { camelToSnake, snakeToCamel } from "utils/camelSnakeTransformation";
import { storage } from "utils/localStorage";
import { useAuthContext } from "./AuthProvider";

const RoleManagerContext = createContext(undefined);

const RoleManagerProvider = ({ children }) => {
  const { switchRole: switchRoleRequest } = useAuthContext();

  const claims = useMemo(() => storage.getDecodedClaims(), []);
  const roleIds = claims?.role_ids ?? [];
  const permIds = claims?.perm_ids ?? [];
  const teamIds = claims?.team_ids ?? [];
  const groupIds = claims?.group_ids ?? [];
  const activeRoleCodename = claims?.active_role ?? null;

  const [currentTeamId, setCurrentTeamIdState] = useState(
    () => storage.getPersistedTeamId() ?? teamIds[0] ?? null
  );

  const { data: roleCatalogue = [] } = useQuery({
    queryKey: ["role-catalogue"],
    queryFn: () => api.users.getRoleCatalogue(),
    select: (res) => res?.data ?? [],
  });

  const { data: resourcePermissions = [] } = useQuery({
    queryKey: ["resource-permissions"],
    queryFn: () => api.users.getResourcePermissions(),
    select: (res) => PermissionsDto.setupResourcePermissionList(res?.data),
  });

  const roles = useMemo(
    () => roleCatalogue.filter((role) => roleIds.includes(role.id)),
    [roleCatalogue, roleIds]
  );

  const currentRole = useMemo(
    () =>
      roles.find((role) => role.codename === activeRoleCodename) ??
      roles[0] ??
      null,
    [roles, activeRoleCodename]
  );

  const currentRoleCodename = currentRole
    ? snakeToCamel(currentRole.codename)
    : null;

  const permissionCodenameById = useMemo(() => {
    const map = new Map();
    resourcePermissions.forEach((permission) => {
      map.set(permission.id, permission.codename);
    });
    return map;
  }, [resourcePermissions]);

  const userPermissionCodenames = useMemo(
    () =>
      new Set(
        permIds
          .map((id) => permissionCodenameById.get(id))
          .filter(Boolean)
      ),
    [permIds, permissionCodenameById]
  );

  const hasPermission = useCallback(
    (featureName) => {
      if (!featureName) return false;
      return userPermissionCodenames.has(camelToSnake(featureName));
    },
    [userPermissionCodenames]
  );

  const permissionByCodename = useMemo(() => {
    const map = new Map();
    resourcePermissions.forEach((permission) => map.set(permission.codename, permission));
    return map;
  }, [resourcePermissions]);

  const getPermission = useCallback(
    (featureName) => {
      if (!featureName) return null;
      return permissionByCodename.get(camelToSnake(featureName)) ?? null;
    },
    [permissionByCodename]
  );

  // No team catalogue exists yet at this layer (teams are nested under
  // Legal Entities, not exposed globally) — expose raw ids until that's
  // resolved; see plan's open question on team context.
  const teams = useMemo(() => teamIds.map((id) => ({ id })), [teamIds]);
  const currentTeam = useMemo(
    () => teams.find((team) => team.id === currentTeamId) ?? null,
    [teams, currentTeamId]
  );

  const setCurrentTeamId = useCallback((teamId) => {
    storage.setPersistedTeamId(teamId);
    setCurrentTeamIdState(teamId);
  }, []);

  const updateCurrentTeam = useCallback(
    (team) => {
      setCurrentTeamId(team?.id ?? null);
    },
    [setCurrentTeamId]
  );

  const setCurrentRole = useCallback(
    (role) => {
      if (role?.codename) {
        switchRoleRequest(role.codename);
      }
    },
    [switchRoleRequest]
  );

  const values = useMemo(
    () => ({
      teams,
      roles,
      groupIds,
      currentTeam,
      currentRole,
      currentRoleCodename,
      userPermissions: userPermissionCodenames,
      setCurrentRole,
      updateCurrentTeam,
      hasPermission,
      getPermission,
    }),
    [
      teams,
      roles,
      groupIds,
      currentTeam,
      currentRole,
      currentRoleCodename,
      userPermissionCodenames,
      setCurrentRole,
      updateCurrentTeam,
      hasPermission,
      getPermission,
    ]
  );

  return (
    <RoleManagerContext.Provider value={values}>
      {children}
    </RoleManagerContext.Provider>
  );
};

const useRoleManager = () => {
  const context = useContext(RoleManagerContext);

  if (!context) {
    throw new Error(
      "RoleManagerContext must be used within a RoleManagerProvider"
    );
  }
  return context;
};

export { RoleManagerProvider, useRoleManager };
